import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiFileText } from 'react-icons/fi';
import { supabase } from '../../supabase/supabase';
import { useAuth } from '../../contexts/AuthContext';
import './TransactionDetails.css';
import FloatingSidebarButton from "../../components/FloatingSidebarButton/FloatingSidebarButton";
import { useLanguage } from "../../contexts/LanguageContext";


const fallbackAvatar = '/default-avatar.png';

const toDate = (ts) => {
  if (!ts) return null;
  if (typeof ts?.toDate === 'function') return ts.toDate();
  const d = new Date(ts);
  return isNaN(d.getTime()) ? null : d;
};

const formatDateTime = (timestamp) => {
  const date = toDate(timestamp);
  if (!date) return '';
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${dd}-${mm}-${yyyy} @ ${hh}:${min}`;
};

const formatAmount = (amount) => {
  const num = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
  return Number.isFinite(num) ? num.toFixed(3) : '0.000';
};
const formatUSD = (winiAmount, winiToUsdRate) => {
  if (!winiToUsdRate) return '0.00';
  const num = typeof winiAmount === 'string' ? parseFloat(winiAmount) : Number(winiAmount);
  const usdAmount = num * winiToUsdRate;
  return Number.isFinite(usdAmount) ? usdAmount.toFixed(2) : '0.00';
};

export default function TransactionDetails() {
  const navigate = useNavigate();
  const { transactionId } = useParams();
  const { currentUser, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [transaction, setTransaction] = useState(null);
  const [winiToUsdRate, setWiniToUsdRate] = useState(null);
  const { translations: t } = useLanguage();


  // sender profile
  const [senderName, setSenderName] = useState('');
  const [senderAvatar, setSenderAvatar] = useState(fallbackAvatar);

  // printable area ref
  const printRef = useRef(null);

  useEffect(() => {
    if (!currentUser?.id || !transactionId) {
      setLoading(false);
      return;
    }

    const fetchTransactionDetails = async () => {
      try {
        setLoading(true);

        // Fetch winiToUsdRate from app_settings
        const { data: appSettings, error: settingsError } = await supabase
          .from('app_settings')
          .select('wini_to_usd_rate')
          .limit(1)
          .single();

        if (!settingsError && appSettings) {
          setWiniToUsdRate(appSettings.wini_to_usd_rate || null);
        }

        // Fetch transaction from Supabase
        const { data: txData, error: txError } = await supabase
          .from('transactions')
          .select('*')
          .eq('id', transactionId)
          .eq('user_id', currentUser.id)
          .single();

        if (txError || !txData) {
          setTransaction(null);
          setLoading(false);
          return;
        }

        // Map Supabase fields to expected format
        const mappedTransaction = {
          id: txData.id,
          source: txData.source || 'wallet',
          flow: txData.flow || 'in',
          amount: txData.amount || 0,
          oldBalance: txData.old_balance || 0,
          newBalance: txData.new_balance || 0,
          // Calculate winicoinsAdded and winicoinsRemoved based on flow
          winicoinsAdded: txData.flow === 'in' ? Math.abs(txData.amount || 0) : 0,
          winicoinsRemoved: txData.flow === 'out' ? Math.abs(txData.amount || 0) : 0,
          time: txData.created_at || txData.updated_at,
          sender_id: txData.sender_id,
          receiver_id: txData.receiver_id,
        };

        setTransaction(mappedTransaction);

        // Fetch sender profile if sender_id exists
        if (txData.sender_id && typeof txData.sender_id === 'string') {
          try {
            const { data: senderProfile, error: profileError } = await supabase
              .from('profiles')
              .select('display_name, username, avatar_url')
              .eq('user_id', txData.sender_id)
              .single();

            if (!profileError && senderProfile) {
              const name = senderProfile.display_name || senderProfile.username || 'User';
              setSenderName(txData.sender_id === currentUser.id ? 'You' : name);
              
              // Resolve avatar URL
              if (senderProfile.avatar_url) {
                try {
                  const path = `${txData.sender_id}/${senderProfile.avatar_url}`;
                  const { data } = supabase.storage.from('avatars').getPublicUrl(path);
                  setSenderAvatar(data?.publicUrl || fallbackAvatar);
                } catch {
                  setSenderAvatar(fallbackAvatar);
                }
              } else {
                setSenderAvatar(fallbackAvatar);
              }
            } else {
              // sender_id might not be a user (e.g., "Winto LLC")
              setSenderName(String(txData.sender_id));
              setSenderAvatar(fallbackAvatar);
            }
          } catch {
            setSenderName(String(txData.sender_id));
            setSenderAvatar(fallbackAvatar);
          }
        } else {
          // Use source as sender name if no sender_id
          setSenderName(txData.source || '—');
          setSenderAvatar(fallbackAvatar);
        }
      } catch (e) {
        console.error('Error fetching transaction details:', e);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactionDetails();
  }, [transactionId, currentUser?.id]);

  const handleExportPDF = () => {
    // Simple, dependency-free export via the browser print dialog.
    // The print CSS below ensures only the card content is printed nicely.
    window.print();
  };

  if (loading) {
    return (
      <div className="transaction-details-page">
        {t.transactionDetails?.loading || "Loading..."}
        </div>
    );
  }

  if (!transaction) {
    return (
      <div className="transaction-details-page">
        <div className="transaction-details-error">
        <p>{t.transactionDetails?.notFound || "Transaction not found"}</p>
        </div>
      </div>
    );
  }

  const isIncoming = transaction.flow === 'in';
  const winisAdded = formatAmount(transaction.winicoinsAdded || 0);
  const winisRemoved = formatAmount(transaction.winicoinsRemoved || 0);
  const oldBalance = formatAmount(transaction.oldBalance || 0);
  const newBalance = formatAmount(transaction.newBalance || 0);

  return (
    <div className="transaction-details-page">
      <FloatingSidebarButton userProfile={userProfile} />
      {/* Header */}
      <div className="transaction-details-header">
        <button
          className="transaction-details-back"
          onClick={() => navigate(-1)}
          aria-label={t.common?.back || "Back"}
          title={t.common?.back || "Back"}
        >
          <FiArrowLeft />
        </button>
        {t.transactionDetails?.title || "Inspect Transaction"}
        <button
          className="transaction-details-pdf"
          aria-label={t.transactionDetails?.exportPdf || "Export PDF"}
          title={t.transactionDetails?.exportPdf || "Export PDF"}
          onClick={handleExportPDF}
        >
          <FiFileText />
        </button>
      </div>

      {/* Printable area */}
      <div className="transaction-details-card" ref={printRef} id="print-area">
        {/* Header row with avatar & From */}
        <div className="td-head">
          <div className="td-avatar">
            <img src={senderAvatar} alt={senderName || 'Profile'} />
          </div>
          <div className="td-from-wrap">
          <div className="td-label">{t.transactionDetails?.from || "From"} :</div>
            <div className="td-from-name">{senderName}</div>
          </div>
        </div>

        <div className="td-sep" />

        {/* Source */}
        <div className="td-row compact">
        <span className="td-label">{t.transactionDetails?.source || "Source"} :</span>
        <span className="td-value">
          {t.transactionDetails?.sources?.[transaction.source] ||
          transaction.source ||
          t.transactionDetails?.sources?.wallet ||
          "wallet"}
        </span>
        </div>

        {/* Flow */}
        <div className="td-row compact">
        <span className="td-label">{t.transactionDetails?.flow || "Flow"} :</span>
        <span className={`td-flow ${isIncoming ? 'in' : 'out'}`}>
          {isIncoming ? (t.transactionDetails?.flows?.in || "in")
                      : (t.transactionDetails?.flows?.out || "out")}
        </span>
        </div>

        {/* Winis Added */}
        <div className="td-row tight">
        <span className="td-label">{t.transactionDetails?.winisAdded || "Winis Added"} :</span>
        <div className="td-box positive">
            <div className="td-usd">{formatUSD(transaction.winicoinsAdded || 0, winiToUsdRate)} USD</div>
            <div className="td-wini">{winisAdded} Winis</div>
          </div>
        </div>

        {/* Winis Removed */}
        <div className="td-row tight">
        <span className="td-label">{t.transactionDetails?.winisRemoved || "Winis Removed"} :</span>
        <div className="td-box negative">
            <div className="td-usd">{formatUSD(transaction.winicoinsRemoved || 0, winiToUsdRate)} USD</div>
            <div className="td-wini">{winisRemoved} Winis</div>
          </div>
        </div>

        {/* Old Balance */}
        <div className="td-row tight">
        <span className="td-label">{t.transactionDetails?.oldBalance || "Old Balance"} :</span>
        <div className="td-box neutral">
            <div className="td-usd">{formatUSD(transaction.oldBalance || 0, winiToUsdRate)} USD</div>
            <div className="td-wini">{oldBalance} Winis</div>
          </div>
        </div>

        {/* New Balance */}
        <div className="td-row tight">
        <span className="td-label">{t.transactionDetails?.newBalance || "New Balance"} :</span>
        <div className="td-box positive">
            <div className="td-usd">{formatUSD(transaction.newBalance || 0, winiToUsdRate)} USD</div>
            <div className="td-wini">{newBalance} Winis</div>
          </div>
        </div>
      </div>

      {/* Footer timestamp chip (tap to PDF) */}
      <div className="td-footer">
        <button className="td-time-chip" onClick={handleExportPDF}>
          {formatDateTime(transaction.time)}
        </button>
      </div>
    </div>
  );
}
